import {
	dateFormats,
	EventIdentifier,
	FeatureTeamEvent,
	type TrackingEventType,
	type TrackingEventTypeWEB,
	useAnalytics,
	useIntl,
	useSettings
} from '@capsicum/common';
import { type NCWBalanceType, usePartners } from '@capsicum/socios-core';
import { useCallback, useMemo, useState } from 'react';

import { ProductTypes } from '../../data-access';
import { useProductCheckout } from '../useCheckout';
import { useProduct } from '../useProduct';

import { useProductCard } from './useProductCard';
import { useProductOrder } from './useProductOrder';
import { useProductOrderDetails } from './useProductOrderDetails';
import { useProductPageTexts } from './useProductPageTexts';
import { useProductPricing } from './useProductPricing';
import { useProductReverseAuction } from './useProductReverseAuction';
import { useProductUpdateFromWS } from './useProductUpdateFromWS';

type UseProductPageProps = {
	getTokenBalanceBy?: NCWBalanceType['getTokenBalanceBy'];
	variantId: string;
};

export const useProductPage = ({ getTokenBalanceBy = () => undefined, variantId }: UseProductPageProps) => {
	const { getLocalizedField } = useIntl();
	const analytics = useAnalytics();
	const [showLimitError, setShowLimitError] = useState<boolean>(true);

	const {
		settings: { rewardsLinks }
	} = useSettings();
	const faqLink = rewardsLinks.fanshopFaq;

	const {
		allImages,
		getCategory,
		getMainImage,
		getSsuAmount,
		getTokenRequired,
		isFetching,
		isLoading,
		refetchProduct,
		variant
	} = useProduct({
		variantId
	});

	const { hasEnoughFanTokens, isComingSoon, isPendingCWallet, setIsComingSoon } = useProductCard({
		getTokenBalanceBy,
		variant
	});

	const { getPartnerBy } = usePartners();

	const partnerShortName = useMemo(
		() => getPartnerBy(p => p.id === variant?.product?.partnerId)?.shortName,
		[getPartnerBy, variant?.product?.partnerId]
	);

	const {
		amountNeeded,
		balanceAfterRedeem,
		hasEnoughSsu,
		hasEnoughStock,
		isBalanceBelowZero,
		isEnoughBalance,
		isPackageProduct,
		isRedeemEnabledOrderSummary,
		packageProductPrice,
		packageQuantity,
		partnerWallet,
		productQuantity,
		quantity,
		setQuantity,
		totalPrice
	} = useProductPricing({ getTokenBalanceBy, variantId });

	const isOutOfStock = useMemo(
		() => variant?.redeemableAt && !isComingSoon && !hasEnoughStock,
		[variant, isComingSoon, hasEnoughStock]
	);

	const reverseAuction = useProductReverseAuction({
		getSsuAmount,
		isOutOfStock,
		refetchProduct,
		variantId
	});

	const { countDownString, isReverseAuction } = reverseAuction;

	const isAuction = useMemo(() => variant?.product?.redeemType === 'auction', [variant]);

	const isTicketType = variant?.product?.productType?.name === ProductTypes.Ticket;
	const isPhysicalTicket = variant?.product?.productType?.name === ProductTypes.Physical_Match_Tickets;
	const isMerchandiseType = variant?.product?.productType?.name === ProductTypes.Merchandise;
	const isVideoGreetingsType = variant?.product?.productType?.name === ProductTypes.Video_Greetings;
	const isExperienceType = variant?.product?.productType?.name === ProductTypes.Experience;

	const hasLinkedProductOrder = variant?.hasLinkedProductOrder;
	const hasLinkedProduct = variant?.product.linkedByProducts.length > 0 || variant?.product.linkedProducts.length > 0;

	const hasReachedOrderLimit = useMemo(
		() => (variant?.product?.totalUserProductPurchases || 0) >= (variant?.product?.maxUserPurchase || 0),
		[variant]
	);

	const maxUserPurchase =
		(variant?.product?.maxUserPurchase || 0) - (variant?.product?.totalUserProductPurchases || 0);

	const isRedeemEnabled = useMemo(() => {
		return (
			hasEnoughSsu &&
			!hasReachedOrderLimit &&
			hasEnoughFanTokens() &&
			hasEnoughStock &&
			!isComingSoon &&
			variant?.redeemableAt
		);
	}, [hasEnoughSsu, hasReachedOrderLimit, hasEnoughFanTokens, hasEnoughStock, isComingSoon, variant?.redeemableAt]);

	const productDescription = useMemo(
		() => getLocalizedField('description', variant?.product || {}),
		[variant, getLocalizedField]
	);

	const eventDate = dateFormats.format(variant?.eventDate, 'DD/MM/YYYY');
	const hasEventDate = eventDate && eventDate !== 'Invalid Date';
	const matchPlace = variant?.place;

	const texts = useProductPageTexts({
		amountNeeded,
		partnerShortName
	});

	const mainImage = useMemo(() => getMainImage(variant)?.url || '', [variant, getMainImage]);
	const mainBlurHash = useMemo(() => getMainImage(variant)?.blurHash || '', [variant, getMainImage]);

	useProductUpdateFromWS(variant?.product?.id, () => {
		refetchProduct();
	});

	const {
		completeCheckoutData,
		fieldsWithEmptyValuesError,
		orderPlaced,
		placeOrder,
		placeOrderDetails,
		preparePlaceOrder,
		setOrderPlaced
	} = useProductOrder({ variantId });

	const { getFieldByCategory, merchandiseDetails, recipientName, ticketHolders } = useProductOrderDetails();

	const importantInformationTranslationsArray = useMemo(() => {
		if (isTicketType || isPhysicalTicket) {
			return [
				texts.importantInformationText1Ticket,
				texts.importantInformationText2Ticket,
				texts.importantInformationText3Ticket
			];
		}
		if (isMerchandiseType) {
			return [
				texts.importantInformationText1Merchandise,
				texts.importantInformationText2Merchandise,
				texts.importantInformationText3Merchandise
			];
		}
		if (isExperienceType) {
			return [
				texts.importantInformationText1Experience,
				texts.importantInformationText2Experience,
				texts.importantInformationText3Experience
			];
		}

		return [];
	}, [isTicketType, isPhysicalTicket, isMerchandiseType, texts, isExperienceType]);

	const sendEventAnalytics = useCallback(
		(eventType: TrackingEventType | TrackingEventTypeWEB, params?: Record<string, unknown>) => {
			analytics.sendEvent(eventType, EventIdentifier.ButtonClicked, {
				action: 'reward_click_redeem',
				feature_team: FeatureTeamEvent.RFT,
				partner_id: variant?.product?.partnerId,
				reward_points: getSsuAmount(),
				...params
			});
		},
		[variant, getSsuAmount]
	);

	const { checkoutInfo, startCheckout } = useProductCheckout();

	return {
		allImages,
		amountNeeded,
		balanceAfterRedeem,
		checkoutInfo,
		completeCheckoutData,
		countDownString,
		eventDate,
		faqLink,
		fieldsWithEmptyValuesError,
		getCategory,
		getFieldByCategory,
		getMainImage,
		getSsuAmount,
		getTokenRequired,
		hasEnoughFanTokens,
		hasEnoughStock,
		hasEventDate,
		hasLinkedProduct,
		hasLinkedProductOrder,
		hasReachedOrderLimit,
		importantInformationTranslationsArray,
		isAuction,
		isBalanceBelowZero,
		isComingSoon,
		isEnoughBalance,
		isExperienceType,
		isFetching,
		isLoading,
		isMerchandiseType,
		isOutOfStock,
		isPackageProduct,
		isPendingCWallet,
		isPhysicalTicket,
		isRedeemEnabled,
		isRedeemEnabledOrderSummary,
		isReverseAuction,
		isTicketType,
		isVideoGreetingsType,
		mainBlurHash,
		mainImage,
		matchPlace,
		maxUserPurchase,
		merchandiseDetails,
		orderPlaced,
		packageProductPrice,
		packageQuantity,
		partnerWallet,
		placeOrder,
		placeOrderDetails,
		preparePlaceOrder,
		productDescription,
		productQuantity,
		quantity,
		recipientName,
		refetchProduct,
		sendEventAnalytics,
		setIsComingSoon,
		setOrderPlaced,
		setQuantity,
		setShowLimitError,
		showLimitError,
		startCheckout,
		texts,
		ticketHolders,
		totalPrice,
		variant
	};
};
